<template>
  <ArtworkAlert class="artwork-error" :class="{ 'artwork-error--compact': showStepsUI }">
    <div v-if="(widthError || heightError) && showNewGangSheetErrorMessage">
      <div class="artwork-error__readable-container">
        <div class="flex">
          <div class="artwork-error__readable-icon">
            <UseSvg icon="warning-hexagon" class="artwork-error__warning" />
          </div>
          <div class="content-center">
            Upload a {{ errorDimensionReadable }}, or try our
            <a
              class="artwork-dimensions__link-to-dtf-artwork artwork-error__link"
              @click="handleGangSheetBuilder"
              @click.stop
            >
              Gang Sheet Builder
            </a>
            for easy design layout.
          </div>
        </div>
        <div>
          <div class="artwork-error__readable-text">Uploaded a single design?</div>
          <button
            type="button"
            class="artwork-error__button-try-dtf-transfer-by-size"
            @click="handleDtfTransferBySize"
            @click.stop
          >
            Try DTF Transfers by Size
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="widthError || heightError">
      <b>The artwork must be {{ errorDimension }}.</b>
      <br />
      Please take a look at our
      <a :href="dpiInstructionsUrl" class="artwork-dimensions__link-to-dtf-artwork">
        Artwork DPI guide
      </a>
      for more info.
    </div>
    <div v-else-if="upscaleThresholdError">
      The uploaded image will not print clearly at this size. Reduce your image size, or click
      <a :href="dpiInstructionsUrl" target="_blank" class="artwork-dimensions__error-message-link">
        here
      </a>
      to learn how you can fix this.
    </div>
    <div v-else>
      <p class="artwork-error__generic-error-message" v-html="dimensionErrorMessage" />
    </div>
  </ArtworkAlert>
</template>

<script>
import { mapState, mapActions } from "pinia";
import ArtworkAlert from "components/jiffy/vue-product-details-dtf/ArtworkAlert.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { redirectTo } from "common/utils/browser";
import UseSvg from "common/vue/components/UseSvg.vue";
import { useDtfStore } from "./store";

export default {
  name: "ArtworkError",
  components: { ArtworkAlert, UseSvg },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    currentStore: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState(useDtfStore, [
      "showNewGangSheetErrorMessage",
      "dpiInstructionsUrl",
      "gangSheetArtworkProductLink",
      "showStepsUI"
    ]),
    validationError() {
      return this.artwork.errorMessage;
    },
    heightError() {
      return this.validationError === "gang_sheet_height_error";
    },
    widthError() {
      return this.validationError === "gang_sheet_width_error";
    },
    dimensionErrorMessage() {
      if (this.artwork.retryFailed) return "Image upload failed";

      return this.validationError || this.artwork.uploadError;
    },
    upscaleThresholdError() {
      return this.validationError === "upscale_threshold_exceeded";
    },
    errorDimension() {
      if (this.heightError) {
        return "7200px tall (24″ at 300 DPI)";
      }

      return "6600px wide (22″ at 300 DPI)";
    },
    errorDimensionReadable() {
      if (this.heightError) {
        return "24″ tall (7200px at 300 DPI)";
      }

      return "22″ wide (6600px at 300 DPI)";
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["setShowGangSheetBuilder"]),
    handleDtfTransferBySize() {
      submitDtfEvent("try_dtf_transfer_by_size_clicked", {
        artwork_id: this.artwork.id,
        error_message: this.dimensionErrorMessage
      });

      redirectTo(this.gangSheetArtworkProductLink);
    },
    handleGangSheetBuilder() {
      this.setShowGangSheetBuilder(true);
      submitDtfEvent("gangsheet_builder_opened", {});
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-error {
  margin: 16px 0;

  &--compact {
    margin-bottom: 0;
  }

  &__generic-error-message {
    margin: 0;
  }

  &__readable-container {
    display: flex;
    gap: 10px;
    flex-direction: column;

    @include with-desktop {
      flex-direction: row;
    }
  }

  &__readable-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
    color: $accent-grapefruit;
  }

  &__link {
    cursor: pointer;
    text-decoration: underline;
  }

  &__readable-text {
    text-align: center;
    padding-bottom: 4px;
  }

  &__warning {
    width: 21px;
    height: 21px;
  }

  &__button-try-dtf-transfer-by-size {
    height: 40px;
    width: 100%;
    padding: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: #414192;
    color: #fff;
    font-weight: 700;
    border: none;

    @include with-desktop {
      width: 190px;
    }
  }
}
</style>
